/**
 * Wallet Standard discovery and the three operations the app needs.
 *
 * Solana wallets register themselves through two DOM events (the app
 * announces `wallet-standard:app-ready`, wallets answer or pre-announce with
 * `wallet-standard:register-wallet`). Every feature takes and returns raw
 * bytes, which is exactly what the SDK's message compiler produces, so no
 * wallet or client library is needed; this mirrors the EIP-6963 hook on the
 * EVM side.
 */
import { solanaPubkeyBase58 } from "@stealthos/sdk";
import { fail } from "./common";

export type SolanaChain = `solana:${string}`;

export interface WalletStandardAccount {
  address: string;
  publicKey: Uint8Array;
  chains: readonly string[];
  features: readonly string[];
  label?: string;
  icon?: string;
}

interface ConnectFeature {
  connect(input?: { silent?: boolean }): Promise<{
    accounts: readonly WalletStandardAccount[];
  }>;
}

interface DisconnectFeature {
  disconnect(): Promise<void>;
}

interface EventsFeature {
  on(
    event: "change",
    listener: (properties: {
      accounts?: readonly WalletStandardAccount[];
      chains?: readonly string[];
    }) => void,
  ): () => void;
}

interface SignMessageFeature {
  signMessage(
    ...inputs: { account: WalletStandardAccount; message: Uint8Array }[]
  ): Promise<{ signedMessage: Uint8Array; signature: Uint8Array }[]>;
}

interface SignTransactionFeature {
  signTransaction(
    ...inputs: {
      account: WalletStandardAccount;
      transaction: Uint8Array;
      chain?: SolanaChain;
    }[]
  ): Promise<{ signedTransaction: Uint8Array }[]>;
}

export interface WalletStandardWallet {
  version: string;
  name: string;
  icon: string;
  chains: readonly string[];
  accounts: readonly WalletStandardAccount[];
  features: {
    "standard:connect"?: ConnectFeature;
    "standard:disconnect"?: DisconnectFeature;
    "standard:events"?: EventsFeature;
    "solana:signMessage"?: SignMessageFeature;
    "solana:signTransaction"?: SignTransactionFeature;
    [feature: string]: unknown;
  };
}

interface WalletsApi {
  register(...wallets: WalletStandardWallet[]): () => void;
}

const REGISTER_EVENT = "wallet-standard:register-wallet";
const APP_READY_EVENT = "wallet-standard:app-ready";

/** A wallet the app can drive end to end on at least one Solana chain. */
export function isUsableSolanaWallet(
  wallet: unknown,
): wallet is WalletStandardWallet {
  const candidate = wallet as WalletStandardWallet | null;
  return Boolean(
    candidate &&
    typeof candidate === "object" &&
    typeof candidate.name === "string" &&
    Array.isArray(candidate.chains) &&
    candidate.chains.some((chain) => String(chain).startsWith("solana:")) &&
    candidate.features &&
    typeof candidate.features["standard:connect"]?.connect === "function" &&
    typeof candidate.features["solana:signMessage"]?.signMessage ===
      "function" &&
    typeof candidate.features["solana:signTransaction"]?.signTransaction ===
      "function",
  );
}

/**
 * Discover Wallet Standard wallets. The callback receives the full list each
 * time it changes; the returned function stops listening.
 */
export function subscribeSolanaWallets(
  onWallets: (wallets: WalletStandardWallet[]) => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  const found = new Map<string, WalletStandardWallet>();
  const publish = () => onWallets([...found.values()]);
  const api: WalletsApi = {
    register: (...wallets) => {
      let changed = false;
      for (const wallet of wallets) {
        if (isUsableSolanaWallet(wallet) && !found.has(wallet.name)) {
          found.set(wallet.name, wallet);
          changed = true;
        }
      }
      if (changed) publish();
      return () => {
        for (const wallet of wallets) found.delete(wallet.name);
        publish();
      };
    },
  };
  const onRegister = (event: Event) => {
    const callback = (event as CustomEvent<unknown>).detail;
    if (typeof callback === "function") {
      try {
        (callback as (walletsApi: WalletsApi) => void)(api);
      } catch (error) {
        console.warn("[solana-wallets] a wallet failed to register", error);
      }
    }
  };
  window.addEventListener(REGISTER_EVENT, onRegister);
  try {
    window.dispatchEvent(new CustomEvent(APP_READY_EVENT, { detail: api }));
  } catch (error) {
    console.warn("[solana-wallets] app-ready announcement failed", error);
  }
  return () => window.removeEventListener(REGISTER_EVENT, onRegister);
}

export interface ConnectedSolanaWallet {
  name: string;
  icon: string;
  chain: SolanaChain;
  address: string;
  publicKey: Uint8Array;
  account: WalletStandardAccount;
  wallet: WalletStandardWallet;
}

function accountForChain(
  accounts: readonly WalletStandardAccount[],
  chain: SolanaChain,
): WalletStandardAccount | undefined {
  return accounts.find(
    (account) => account.chains.length === 0 || account.chains.includes(chain),
  );
}

export async function connectSolanaWallet(
  wallet: WalletStandardWallet,
  chain: SolanaChain,
  options: { silent?: boolean } = {},
): Promise<ConnectedSolanaWallet> {
  if (!isUsableSolanaWallet(wallet)) {
    const name = (wallet as { name?: unknown } | null)?.name;
    fail(
      `${typeof name === "string" ? name : "wallet"} cannot sign Solana messages and transactions`,
    );
  }
  if (!wallet.chains.includes(chain)) {
    fail(`${wallet.name} does not support ${chain}`);
  }
  const { accounts } = await wallet.features["standard:connect"]!.connect(
    options.silent ? { silent: true } : undefined,
  );
  const account = accountForChain(accounts, chain);
  if (!account) {
    fail(`${wallet.name} exposed no account for ${chain}`);
  }
  const publicKey = Uint8Array.from(account.publicKey);
  const address = solanaPubkeyBase58(publicKey, "wallet public key");
  if (account.address !== address) {
    fail(
      `${wallet.name} reported an address that does not match its public key`,
    );
  }
  return {
    name: wallet.name,
    icon: wallet.icon,
    chain,
    address,
    publicKey,
    account,
    wallet,
  };
}

export async function disconnectSolanaWallet(
  connected: ConnectedSolanaWallet,
): Promise<void> {
  try {
    await connected.wallet.features["standard:disconnect"]?.disconnect();
  } catch (error) {
    console.warn("[solana-wallets] disconnect failed", error);
  }
}

/** Subscribe to account changes; the listener gets the new account list. */
export function onSolanaWalletChange(
  connected: ConnectedSolanaWallet,
  listener: (accounts: readonly WalletStandardAccount[] | undefined) => void,
): () => void {
  const events = connected.wallet.features["standard:events"];
  if (!events || typeof events.on !== "function") return () => {};
  return events.on("change", (properties) => listener(properties.accounts));
}

export async function signSolanaMessage(
  connected: ConnectedSolanaWallet,
  message: Uint8Array,
): Promise<Uint8Array> {
  const [result] = await connected.wallet.features[
    "solana:signMessage"
  ]!.signMessage({ account: connected.account, message });
  const signature = Uint8Array.from(result?.signature ?? []);
  if (signature.length !== 64) {
    fail(`${connected.name} returned a malformed message signature`);
  }
  return signature;
}

export async function signSolanaTransaction(
  connected: ConnectedSolanaWallet,
  transaction: Uint8Array,
): Promise<Uint8Array> {
  const [result] = await connected.wallet.features[
    "solana:signTransaction"
  ]!.signTransaction({
    account: connected.account,
    transaction,
    chain: connected.chain,
  });
  const signed = Uint8Array.from(result?.signedTransaction ?? []);
  if (signed.length === 0) {
    fail(`${connected.name} returned no signed transaction`);
  }
  return signed;
}
